// The import preview's lines as a tree, and what each line is, for the preview table.
// apps/smart-catalog/frontend/src/pages/bom-intelligence/preview-tree.ts
//
// Rows arrive in tree order with a level (mappedData.level). A line's parent is the nearest
// line above it with a lower level. When the first line is an assembly, the lines at level 0
// after it hang on no assembly: no drawing and no level column placed them, and the preview
// shows them in a group of their own. Pure functions, no React.

import type { ParsedRow } from '../../types';

export type LineKind = 'assembly' | 'make' | 'buy' | 'document';

export interface PreviewTree {
  /** True when the rows carry a structure worth drawing (some line below level 0). */
  hasTree: boolean;
  depthOf: Map<number, number>;
  parentOf: Map<number, number | undefined>;
  childrenOf: Map<number, number[]>;
  /** Level-0 lines that are on no assembly, in file order (empty unless the tree has a root assembly). */
  unplaced: number[];
}

const levelOf = (r: ParsedRow): number => {
  const n = Number(r.mappedData?.level);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

export function lineKind(r: ParsedRow): LineKind {
  if (r.lineType === 'DOCUMENT') return 'document';
  if (r.lineType === 'ASSEMBLY' || r.partClassificationOverride === 'ASSEMBLY') return 'assembly';
  if (r.partClassificationOverride === 'CUSTOM_MACHINED') return 'make';
  return 'buy';
}

export function buildPreviewTree(rows: ParsedRow[]): PreviewTree {
  const depthOf = new Map<number, number>();
  const parentOf = new Map<number, number | undefined>();
  const childrenOf = new Map<number, number[]>();
  const stack: ParsedRow[] = [];
  for (const r of rows) {
    const level = levelOf(r);
    while (stack.length && levelOf(stack[stack.length - 1]) >= level) stack.pop();
    const parent = level > 0 ? stack[stack.length - 1] : undefined;
    parentOf.set(r.rowIndex, parent?.rowIndex);
    depthOf.set(r.rowIndex, parent ? (depthOf.get(parent.rowIndex) ?? 0) + 1 : 0);
    if (parent) childrenOf.set(parent.rowIndex, [...(childrenOf.get(parent.rowIndex) ?? []), r.rowIndex]);
    stack.push(r);
  }
  const hasTree = rows.some((r) => levelOf(r) > 0);
  const root = rows[0];
  const rootIsAssembly = !!root && lineKind(root) === 'assembly' && (childrenOf.get(root.rowIndex)?.length ?? 0) > 0;
  const unplaced = hasTree && rootIsAssembly
    ? rows.slice(1).filter((r) => levelOf(r) === 0 && lineKind(r) !== 'assembly').map((r) => r.rowIndex)
    : [];
  return { hasTree, depthOf, parentOf, childrenOf, unplaced };
}

/** Every line below this one, at any depth. */
export function descendantsOf(tree: PreviewTree, rowIndex: number): number[] {
  const out: number[] = [];
  const walk = (i: number) => {
    for (const c of tree.childrenOf.get(i) ?? []) {
      out.push(c);
      walk(c);
    }
  };
  walk(rowIndex);
  return out;
}

/** The line's quantity in a configuration; undefined = the line is not in it. Without a configuration, its quantity. */
export function quantityIn(r: ParsedRow, configurationKey?: string): number | undefined {
  if (!configurationKey) {
    const q = Number(r.mappedData?.quantity);
    return Number.isFinite(q) ? q : undefined;
  }
  if (lineKind(r) === 'assembly') return 1;
  const q = (r.mappedData?.configurationQuantities as Record<string, number> | undefined)?.[configurationKey];
  return typeof q === 'number' ? q : undefined;
}

export interface LineNote {
  /** A few words for the table. */
  short: string;
  /** The sentence for the detail panel. */
  text: string;
  /** Information only: nothing for the buyer to check. */
  info?: boolean;
}

/** The import's warning, said in a few words. A warning not recognised is shown as it is. */
export function lineNote(warning: string): LineNote | null {
  let m: RegExpMatchArray | null;
  if (/^Placed under /.test(warning)) return null;
  if (/Assembly recovered from its drawing/.test(warning))
    return { short: 'From its drawing', text: 'Found on its assembly drawing; the sheet has no line for it.', info: true };
  if ((m = warning.match(/^Drawing revision differs: the title block says "(.+?)" and the BOM row says "(.+?)"/)))
    return { short: 'Revision differs', text: `The drawing is ${m[1]}; the sheet says ${m[2]}.` };
  if ((m = warning.match(/title block says "(.+?)" but the BOM row says "(.+?)"/)))
    return { short: 'Drawing number differs', text: `The drawing reads ${m[1]}; the sheet says ${m[2]}.` };
  if ((m = warning.match(/Spreadsheet quantity (\S+) differs from the (.+?) parts list \((\S+) per assembly\)/)))
    return { short: 'Qty differs from drawing', text: `${m[2]} lists ${m[3]} per assembly; the sheet says ${m[1]}. The sheet's quantity is kept.` };
  if (/No manufacturer part number found/.test(warning))
    return { short: 'No maker part number', text: 'The description names no maker part number; sellers are searched by the description.' };
  if (/no drawing or model/.test(warning))
    return { short: 'No drawing in package', text: 'Numbered like the package drawings, but no drawing or model came with it.' };
  if ((m = warning.match(/^Drawing (.+) could not be read: (.+)$/)))
    return { short: 'Drawing not readable', text: `${m[1]} could not be read (${m[2]}).` };
  return { short: warning.length > 40 ? `${warning.slice(0, 38)}…` : warning, text: warning };
}

export function lineNotes(r: ParsedRow): Array<LineNote & { error?: boolean }> {
  const errors = (r.errors ?? []).map((e) => ({ short: e.length > 40 ? `${e.slice(0, 38)}…` : e, text: e, error: true }));
  const notes = (r.warnings ?? []).map(lineNote).filter((n): n is LineNote => !!n);
  return [...errors, ...notes];
}

/** A cell holding only question marks: its text was lost when the sheet was saved (Hebrew in a non-Unicode file). */
export const isLostText = (v: unknown): boolean => typeof v === 'string' && /^\?+$/.test(v.trim());
